import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { PriceSummaryLineItem } from '../PriceSummary.types';
import { PriceSummaryCurrencyFormatOptions } from '../ProductPriceSummary.controller';
import { useStationeryProductPricingContext } from '@apps/card/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useGetValidProductShippingOptions } from '@apps/card/routes/CardCustomizer/steps/Review/components/DeliveryMethodSelector/useGetValidProductShippingOptions';
import { CardAddress, CardDelivery, ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useEffect } from 'react';

const isPoBoxAddress = (address: CardAddress | undefined) => {
  if (!address) return false;

  const poBoxRegex = /P(.?\s?O.?)?\s?Box/i;
  return poBoxRegex.test(address.address1);
};

export const useGetCardDeliveryInfo = (
  draft: ThemeJson,
  updateDeliveryMethod?: (newMethod: CardDelivery) => void
): { lineItem: PriceSummaryLineItem; priceInMinorUnits: number; deliveryMethodId: string } | null => {
  const formatPrice = useMinorUnitsToPriceString();
  const { activeProduct } = useStationeryProductPricingContext();

  const deliveryMethodType = draft.customizations.delivery;
  const quantity = draft.customizations.quantity;

  const possibleShippingOptions = useGetValidProductShippingOptions(activeProduct, {
    quantity,
    isPOBox: isPoBoxAddress(draft.customizations.deliveryAddress),
    countryCode: draft.customizations.deliveryAddress?.countryCode ?? ''
  });

  const deliveryMethodConfig = possibleShippingOptions.find(o => o.id === deliveryMethodType) ?? possibleShippingOptions[0];

  // If delivery config ID doesn't match draft (Old delivery method removed),
  // then we have the config for the first possible delivery method.
  // Set draft delivery to match.
  useEffect(() => {
    if (draft.customizations.delivery && deliveryMethodConfig) {
      if (deliveryMethodConfig.id !== deliveryMethodType) {
        updateDeliveryMethod?.(deliveryMethodConfig.id as CardDelivery);
      }
    }
  }, [deliveryMethodConfig, deliveryMethodType, draft.customizations.delivery, updateDeliveryMethod]);

  if (!deliveryMethodConfig) {
    return null;
  }

  const deliveryMethodPrice = deliveryMethodConfig.price;
  const deliveryMethodPriceString = formatPrice(deliveryMethodPrice, PriceSummaryCurrencyFormatOptions);

  return { lineItem: [deliveryMethodConfig.title, deliveryMethodPriceString], priceInMinorUnits: deliveryMethodPrice, deliveryMethodId: deliveryMethodConfig.id };
};
